import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Clock,
  Wallet,
  FileText,
  FolderOpen,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  User,
  Building2,
  CreditCard,
  TestTube, 
  HelpCircle
} from 'lucide-react';

// ============================================================================
// 🔥 IMPORTS
// ============================================================================
import { useAuth } from '../contexts/AuthContext';
import useCompanyParams from '../hooks/useCompanyParams';
import api from '../services/api';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useRights } from '../contexts/RightsContext';
import FullscreenLoader from '../pages/FullscreenLoader';
import AideGlobale from './AideGlobale';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(true); // Plié par défaut
  const [hoveredItem, setHoveredItem] = useState(null); // Pour les tooltips
  const [showLogoutLoader, setShowLogoutLoader] = useState(false);
  const [showAide, setShowAide] = useState(false);

  // ============================================================================
  // DROITS DEPUIS LE CONTEXTE GLOBAL
  // ============================================================================
  const { rights: userRights, loading: loadingRights } = useRights();

  // ============================================================================
  // AUTH & COMPANY
  // ============================================================================
  const { user, company, logout: authLogout } = useAuth();
  const { companyInfo, loading } = useCompanyParams();

  // ============================================================================
  // VÉRIFIER LES FEATURE FLAGS
  // ============================================================================
const { isEnabled: rfidEnabled } = useFeatureFlag('rfid_attendance');
const { isEnabled: comptaEnabled } = useFeatureFlag('compta_module');
const { isEnabled: baseDocEnabled } = useFeatureFlag('base_documentaire');

  // ============================================================================
  // MENU ITEMS STANDARDS
  // ============================================================================
  const allMenuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/dashboard', key: 'dashboard' },
    { icon: Users, label: 'Ressources Humaines', path: '/employees', key: 'employees' },
    { icon: Clock, label: 'Carnet de pointage', path: '/attendance', key: 'attendance' },
    { icon: Wallet, label: 'Compta & Finance', path: '/payroll', key: 'payroll', featureFlag: 'compta_module' },
    { icon: FileText, label: 'Rapport global', path: '/reports', key: 'reports' },
    { icon: FolderOpen, label: 'Base Documentaire', path: '/documents', key: 'documents', featureFlag: 'base_documentaire' },
    { icon: Settings, label: 'Paramètres', path: '/settings', key: 'settings' }
  ];


 

  // ============================================================================
  // ITEMS CONDITIONNELS (BASÉS SUR FEATURE FLAGS)
  // ============================================================================
  const conditionalMenuItems = [];

  // ============================================================================
  // FILTRER LES MENUS SELON LES DROITS + FEATURE FLAGS
  // ============================================================================
  const menuItems = (() => {
  // Filtrer les menus standards selon droits ET feature flags
  let items = allMenuItems.filter(item => {
    if (loadingRights) return false;
    

    // 🔥 Si l'item a un featureFlag, vérifier qu'il est activé
    if (item.featureFlag === 'compta_module') {
    if (!comptaEnabled) return false;
    }
    if (item.featureFlag === 'base_documentaire') {
      if (!baseDocEnabled) return false;
    }
      


    // Vérifier les droits normalement
    if (user?.role === 'admin') return true;
    return userRights?.[item.key] === true;
  });

  // Ajouter les items conditionnels (RFID, etc.)
 // Ajouter les items conditionnels (RFID, etc.)
  conditionalMenuItems.forEach(item => {
    if (item.featureFlag === 'rfid_attendance' && rfidEnabled) {
      if (user?.role === 'admin' || (item.requiresRight && userRights?.[item.requiresRight])) {
        items.push(item);
      }
    }
  });

 

  return items;
})();

  // ============================================================================
  // LOGOUT
  // ============================================================================
 const handleLogout = () => {
  setShowLogoutLoader(true);
};

  const isActive = (path) => location.pathname === path;

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div
      className={`${
        isCollapsed ? 'w-20' : 'w-64'
      } h-screen text-white flex flex-col transition-all duration-300 shadow-xl relative`}
      style={{ backgroundColor: '#448696', overflow: 'visible', zIndex: 50 }}
    >
      {/* Bouton collapse/expand */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-8 text-white p-1 rounded-full shadow-md hover:shadow-lg transition-all duration-200 z-50 opacity-40 hover:opacity-100"
        style={{ backgroundColor: 'rgba(254, 215, 116, 0.9)' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fed774'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(254, 215, 116, 0.9)'; }}
      >
        {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      {/* Logo / Titre */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          {!isCollapsed ? (
            <>
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 p-1 shadow-lg"
                style={{ backgroundColor: '#ffffff' }}
              >
                {loading ? (
                  <div className="animate-pulse w-full h-full rounded-lg bg-gray-300"></div>
                ) : companyInfo.logo_url ? (
                  <img 
                    src={companyInfo.logo_url}
                    alt="Logo entreprise"
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                ) : (
                  <div 
                    className="w-full h-full flex items-center justify-center font-bold text-xl rounded-lg"
                    style={{ color: companyInfo.couleur_logo || '#448696' }}
                  >
                    {companyInfo.logo_initiales || company?.name?.substring(0, 2).toUpperCase() || 'HP'}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                {loading ? (
                  <>
                    <div className="h-6 bg-white/20 rounded animate-pulse mb-1"></div>
                    <div className="h-4 bg-white/10 rounded animate-pulse w-2/3"></div>
                  </>
                ) : (
                  <>
                    <h1 className="text-xl font-bold truncate leading-tight">
                      {companyInfo.nom || company?.name || 'HoraPilot'}
                    </h1>
                    <p className="text-sm text-white/70 font-medium truncate">
                      {companyInfo.description || 'Gestion RH'}
                    </p>
                  </>
                )}
              </div>
            </>
          ) : (
           <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 p-1 shadow-lg mx-auto"
              style={{ backgroundColor: '#ffffff' }}
              title={companyInfo.nom || company?.name || 'HoraPilot'}
            >
              {loading ? (
                <div className="animate-pulse w-full h-full rounded-lg bg-gray-300"></div>
              ) : companyInfo.logo_url ? (
                <img 
                  src={companyInfo.logo_url}
                  alt="Logo"
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
              ) : (
                <div 
                  className="w-full h-full flex items-center justify-center font-bold text-lg rounded-lg"
                  style={{ color: companyInfo.couleur_logo || '#448696' }}
                >
                  {companyInfo.logo_initiales || company?.name?.substring(0, 2).toUpperCase() || 'HP'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Menu de navigation */}
      <nav className="flex-1 py-6 px-3 overflow-y-auto custom-scrollbar" style={{ overflow: isCollapsed ? 'visible' : undefined }}>
        {loadingRights ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-white/70 text-sm">Chargement...</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const isTestItem = item.adminOnly;
              const isConditionalItem = item.featureFlag;
              const isHovered = hoveredItem === item.key;

              return (
                <li 
                  key={item.path} 
                  className="animate-slideIn" 
                  style={{ animationDelay: `${index * 50}ms`, position: 'relative' }}
                  onMouseEnter={() => isCollapsed && setHoveredItem(item.key)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <button
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      active
                        ? 'text-gray-900 shadow-lg'
                        : 'text-white hover:bg-white/10'
                    } ${isCollapsed ? 'justify-center' : ''}`}
                    style={{
                      backgroundColor: active ? '#fed774' : 'transparent'
                    }}
                  >
                    <Icon size={20} className="flex-shrink-0" />
                    {!isCollapsed && (
                      <>
                        <span className="font-semibold text-sm">{item.label}</span>
                        
                        {isTestItem && (
                          <span className="ml-auto text-xs bg-yellow-400/30 text-yellow-200 px-2 py-0.5 rounded-full border border-yellow-400/50">
                            TEST
                          </span>
                        )}
                        
                        {/*
                        {isConditionalItem && !isTestItem && (
                          <span className="ml-auto text-xs bg-green-400/30 text-green-200 px-2 py-0.5 rounded-full border border-green-400/50">
                            BETA
                          </span>
                        )} */}
                        
                        {!isTestItem && !isConditionalItem && active && (
                          <div className="ml-auto w-2 h-2 rounded-full" style={{ backgroundColor: '#448696' }}></div>
                        )}
                      </>
                    )}
                  </button>

                  {/* Tooltip stylé (mode plié) */}
                  {isCollapsed && isHovered && (
                    <div
                      style={{
                        position: 'fixed',
                        left: '84px',
                        top: 'auto',
                        padding: '8px 16px',
                        borderRadius: '10px',
                        background: '#448696',
                        color: '#fdfcf5',
                        fontSize: '13px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        zIndex: 200,
                        boxShadow: '0 4px 20px rgba(68,134,150,0.4), 0 0 0 1px rgba(255,255,255,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        pointerEvents: 'none',
                        animation: 'tooltipIn 0.15s ease forwards',
                        marginTop: '-36px',
                      }}
                    >
                      {/* Flèche */}
                      <div style={{
                        position: 'absolute',
                        left: '-5px',
                        top: '50%',
                        transform: 'translateY(-50%) rotate(45deg)',
                        width: '10px',
                        height: '10px',
                        background: '#448696',
                        borderRadius: '2px',
                      }} />
                      <span>{item.label}</span>
                      {isTestItem && (
                        <span style={{
                          fontSize: '9px', fontWeight: '700', textTransform: 'uppercase',
                          letterSpacing: '0.5px', padding: '2px 6px', borderRadius: '6px',
                          background: 'rgba(254,215,116,0.25)', color: '#fed774',
                          border: '1px solid rgba(254,215,116,0.4)',
                        }}>TEST</span>
                      )}
                    {/*  {isConditionalItem && !isTestItem && (
                        <span style={{
                          fontSize: '9px', fontWeight: '700', textTransform: 'uppercase',
                          letterSpacing: '0.5px', padding: '2px 6px', borderRadius: '6px',
                          background: 'rgba(74,222,128,0.2)', color: '#86efac',
                          border: '1px solid rgba(74,222,128,0.35)',
                        }}>BETA</span>
                      )} */}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      {/* Profil utilisateur */}
     {/* Bouton Aide */}
      <div
        className="px-3 pb-2"
        style={{ position: 'relative' }}
        onMouseEnter={() => isCollapsed && setHoveredItem('aide')}
        onMouseLeave={() => setHoveredItem(null)}
      >
        <button
          onClick={() => setShowAide(true)}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-white hover:bg-white/10 ${
            isCollapsed ? 'justify-center' : ''
          }`}
        >
          <HelpCircle size={20} className="flex-shrink-0" />
          {!isCollapsed && <span className="font-semibold text-sm">Centre d'aide</span>}
        </button>

        {/* Tooltip mode plié */}
        {isCollapsed && hoveredItem === 'aide' && (
          <div
            style={{
              position: 'fixed',
              left: '84px',
              padding: '8px 16px',
              borderRadius: '10px',
              background: '#2d6a7f',
              color: '#fdfcf5',
              fontSize: '13px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              zIndex: 200,
              boxShadow: '0 4px 20px rgba(45,106,127,0.4), 0 0 0 1px rgba(255,255,255,0.15)',
              pointerEvents: 'none',
              animation: 'tooltipIn 0.15s ease forwards',
              marginTop: '-36px',
            }}
          >
            <div style={{
              position: 'absolute',
              left: '-5px',
              top: '50%',
              transform: 'translateY(-50%) rotate(45deg)',
              width: '10px',
              height: '10px',
              background: '#2d6a7f',
              borderRadius: '2px',
            }} />
            Centre d'aide
          </div>
        )}
      </div>

      {/* Profil utilisateur */}
      <div className="p-4 border-t border-white/10">
        <div
          className={`flex items-center gap-3 mb-3 p-3 rounded-xl ${
            isCollapsed ? 'justify-center' : ''
          }`}
          style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
        >
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full shadow-sm" style={{ backgroundColor: '#fed774' }}>
            <User size={20} className="text-gray-900" />
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate text-sm">
                {user?.name || 'Utilisateur'}
              </p>
              <p className="text-xs text-white/70 truncate">{user?.email || ''}</p>
            </div>
          )}
        </div>

        {/* Bouton déconnexion */}
        <div 
          style={{ position: 'relative' }}
          onMouseEnter={() => isCollapsed && setHoveredItem('logout')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <button
            onClick={handleLogout}
            className={`sidebar-logout w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
              isCollapsed ? 'justify-center' : ''
            }`}
          >
            <LogOut size={20} className="flex-shrink-0" />
            {!isCollapsed && <span className="font-semibold text-sm">Déconnexion</span>}
          </button>

          {/* Tooltip déconnexion */}
          {isCollapsed && hoveredItem === 'logout' && (
            <div
              style={{
                position: 'fixed',
                left: '84px',
                top: 'auto',
                padding: '8px 16px',
                borderRadius: '10px',
                background: '#dc2626',
                color: '#fdfcf5',
                fontSize: '13px',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                zIndex: 200,
                boxShadow: '0 4px 20px rgba(220,38,38,0.4), 0 0 0 1px rgba(255,255,255,0.15)',
                pointerEvents: 'none',
                animation: 'tooltipIn 0.15s ease forwards',
                marginTop: '-36px',
              }}
            >
              <div style={{
                position: 'absolute',
                left: '-5px',
                top: '50%',
                transform: 'translateY(-50%) rotate(45deg)',
                width: '10px',
                height: '10px',
                background: '#dc2626',
                borderRadius: '2px',
              }} />
              <span>Déconnexion</span>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes tooltipIn {
          from { opacity: 0; transform: translateX(-4px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out forwards;
          opacity: 0;
        }

        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.2); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.3); }

        /* ── Bouton déconnexion ── */
        .sidebar-logout {
          background: rgba(220, 38, 38, 0.12);
          color: #fca5a5;
          border: 1px solid rgba(220, 38, 38, 0.15);
        }
        .sidebar-logout:hover {
          background: rgba(220, 38, 38, 0.25);
          color: #fdfcf5;
          border-color: rgba(220, 38, 38, 0.35);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
        }
      `}</style>


      <FullscreenLoader
        visible={showLogoutLoader}
        duration={1600}
        message="Au revoir, À bientôt !"
        onComplete={() => {
          localStorage.removeItem('horapilot_rights');
          sessionStorage.removeItem('kioskMode');
          sessionStorage.removeItem('pointage_viewMode');
          authLogout();
          navigate('/login');
        }}
      />

      {/* Panneau aide */}
      {showAide && (
        <AideGlobale onClose={() => setShowAide(false)} />
      )}
    </div>
  );
};

export default Sidebar;